import { useMemo, useState } from "react";
import { useShareGate } from "@/hooks/useShareGate";
import { ToolContainer } from "@/components/ToolLayout";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogUsageOnAction } from "@/hooks/useLogUsageOnAction";
import { Checkbox } from "@/components/ui/checkbox";
import { downloadBlob } from "@/components/ToolLayout";
import { JsonTree } from "./JsonTree";
import { parseJson, run, repair, detectKind, type Action, type Options, type ParseError, type Kind } from "./jsonUtils";

const TOOL_INFO_CONTENT = {
  what: `Formats and validates JSON (or YAML) data, making dense or minified data readable with proper indentation, and flagging syntax errors.`,
  how: `Paste or upload JSON or YAML and choose Format, Minify or Convert. YAML is detected automatically. When you format or minify JSON, large numbers and values like 1.10 are kept exactly as written; converting to or from YAML turns them into plain numbers, so 1.10 becomes 1.1. Syntax errors show the line and column, with a one-click repair for trailing commas, single quotes and comments.`,
  tip: `If you're debugging an API response that looks like a wall of text, pasting it here first to format and validate it is usually the fastest way to actually spot what's wrong with the data.`,
};

export default function JsonFormatter() {
  const { gateShare } = useShareGate();
  const [input, setInput] = useState("");
  const [output, setOutput] = useState("");
  const [outKind, setOutKind] = useState<"json" | "yaml">("json");
  const [error, setError] = useState<ParseError | null>(null);
  const [canRepair, setCanRepair] = useState(false);
  const [indent, setIndent] = useState<Options["indent"]>(2);
  const [sortKeys, setSortKeys] = useState(false);
  const [view, setView] = useState<"text" | "tree">("text");

  const { logUsageNow } = useLogUsageOnAction();

  // The tree is built from the output, so it always matches what Copy and Download give.
  const tree = useMemo(() => { if (!output || outKind !== "json") return undefined; try { return parseJson(output); } catch { return undefined; } }, [output, outKind]);

  const detected: Kind | null = input.trim() ? detectKind(input) : null;

  // Gated before the work so a blocked click produces neither a result nor an
  // error. The success toast only appears when the input actually parsed.
  const go = async (action: Action, text = input) => {
    const allowed = await logUsageNow();
    if (!allowed) return;
    const r = run(text, action, { indent, sortKeys });
    if (r.ok) {
      setOutput(r.output);
      setOutKind(action === "convert" && r.kind === "json" ? "yaml" : "json");
      setError(null);
      setCanRepair(false);
      toast.success(action === "minify" ? "JSON minified" : action === "convert" ? "Converted" : "Formatted");
    } else {
      setOutput("");
      setError(r.error);
      setCanRepair(r.repairable);
    }
  };

  const fix = async () => {
    const fixed = repair(input);
    if (fixed === null) return;
    setInput(fixed);
    await go("format", fixed);
  };

  const onFile = async (f: File | undefined) => {
    if (!f) return;
    setInput(await f.text());
    setError(null);
    setOutput("");
  };

  return (
    <ToolContainer>
      <div className="space-y-4">
        <div>
          <div className="flex items-center justify-between mb-1">
            <Label>Input JSON or YAML{detected && ` (detected: ${detected.toUpperCase()})`}</Label>
            <label className="text-xs underline cursor-pointer text-muted-foreground">
              Upload file
              <input type="file" accept=".json,.yaml,.yml,.txt,application/json" className="hidden" onChange={e => onFile(e.target.files?.[0])} />
            </label>
          </div>
          <Textarea
            value={input}
            onChange={e => { setInput(e.target.value); setError(null); }}
            placeholder='{"key": "value"}'
            className="min-h-[150px] font-mono text-sm"
          />
        </div>
        {error && (
          <div className="rounded-lg border border-destructive/50 p-3 text-sm" role="alert">
            <p className="text-destructive font-medium">
              {error.message}
              {error.line ? ` (line ${error.line}, column ${error.column})` : ""}
            </p>
            {error.lineText !== undefined && error.line && (
              <pre className="mt-2 overflow-x-auto rounded bg-destructive/10 px-2 py-1 font-mono text-xs">
                {error.line}: {error.lineText}
                {"\n"}
                {" ".repeat(String(error.line).length + 2 + Math.max(0, (error.column ?? 1) - 1))}^
              </pre>
            )}
            {canRepair && (
              <Button size="sm" variant="outline" className="mt-2" onClick={fix}>
                Fix it for me (trailing commas, single quotes, comments)
              </Button>
            )}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
          <label className="flex items-center gap-2">
            Indent
            <select
              className="border border-border rounded-md bg-background px-2 py-1"
              value={String(indent)}
              onChange={e => setIndent(e.target.value === "tab" ? "tab" : (Number(e.target.value) as 2 | 4))}
            >
              <option value="2">2 spaces</option>
              <option value="4">4 spaces</option>
              <option value="tab">Tab</option>
            </select>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox checked={sortKeys} onCheckedChange={v => setSortKeys(v === true)} />
            Sort keys
          </label>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => go("format")}>Format / Beautify</Button>
          <Button variant="outline" onClick={() => go("minify")}>Minify</Button>
          <Button variant="outline" onClick={() => go("convert")}>
            {detected === "yaml" ? "YAML → JSON" : "JSON → YAML"}
          </Button>
        </div>
        {output && (
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label>Output</Label>
              {outKind === "json" && tree !== undefined && (
                <div className="flex gap-1 text-xs">
                  <Button size="sm" variant={view === "text" ? "default" : "outline"} className="h-7" onClick={() => setView("text")}>Text</Button>
                  <Button size="sm" variant={view === "tree" ? "default" : "outline"} className="h-7" onClick={() => setView("tree")}>Tree</Button>
                </div>
              )}
            </div>
            {view === "tree" && outKind === "json" && tree !== undefined
              ? <JsonTree value={tree} />
              : <Textarea value={output} readOnly className="min-h-[150px] font-mono text-sm" />}
            <div className="flex gap-2 mt-2">
              <Button size="sm" onClick={() => { void gateShare(async () => { try { await navigator.clipboard.writeText(output); toast.success("Copied!"); return true; } catch { toast.error("Couldn't copy. Select the text and copy it by hand."); return false; } }); }}>Copy</Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => downloadBlob(new Blob([output], { type: "text/plain" }), outKind === "yaml" ? "output.yaml" : "output.json")}
              >
                Download
              </Button>
            </div>
          </div>
        )}
      </div>
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
